"use client";

import {
  Content,
  Provider,
  Root,
  Trigger,
  Arrow,
  Portal,
} from "@radix-ui/react-tooltip";
import { cn } from "@drpcrd/utils/shared";
import {
  ComponentPropsWithoutRef,
  ComponentRef,
  ReactNode,
  forwardRef,
} from "react";
import { Typography } from "./typography";

export const TooltipProvider = Provider;

export const TooltipRoot = Root;

export const TooltipTrigger = Trigger;

export const TooltipContent = forwardRef<
  ComponentRef<typeof Content>,
  ComponentPropsWithoutRef<typeof Content>
>(({ className, sideOffset = 10, ...props }, ref) => (
  <Portal>
    <Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "bg-primary-800 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[9999] max-w-14 overflow-hidden rounded-md px-1 py-0.75 text-center text-sm text-white",
        className,
      )}
      {...props}
    >
      {props.children}
      <Arrow className="fill-primary-800 h-1.25 w-2.75" />
    </Content>
  </Portal>
));
TooltipContent.displayName = Content.displayName;

type TooltipProps = {
  content: string | ReactNode;
  children: ReactNode;
  delayDuration?: number;
  className?: string;
};

/**
 * Simple tooltip wrapper. Pass `content` as a string or ReactNode for the tooltip body and `children` as the trigger.
 * Use `className` to customize the tooltip content styling (e.g., increase max-width).
 */
export const Tooltip = ({
  content,
  children,
  delayDuration = 0,
  className,
}: TooltipProps) => (
  <Provider delayDuration={delayDuration}>
    <Root>
      <Trigger asChild>
        <span>{children}</span>
      </Trigger>
      <TooltipContent className={className}>
        {typeof content === "string" ? (
          <Typography
            size="xs"
            className="text-center whitespace-pre-line text-white normal-case"
          >
            {content}
          </Typography>
        ) : (
          content
        )}
      </TooltipContent>
    </Root>
  </Provider>
);
